import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import './TeacherRegistrationPage.css';

const languageOptions = ['Hindi', 'Marathi', 'English', 'Punjabi', 'Kannada', 'Telugu'];
const roleOptions = [
  'Principal/ HOS / HM / School Head',
  'Head Teacher/Incharge Head Teacher',
  'Nodal Teacher',
  'Teacher',
  'Govt Nodal Officer',
  'Mentor Teachers',
  'Zonal Coordinators',
  'Deputy Director Education',
  'District Coordinator'
];
const genderOptions = ['Male', 'Female', 'Others'];
const gradeOptions = [
  'NA',
  ...Array.from({ length: 10 }, (_, index) => String(index + 3)),
];

function TeacherRegistrationPage({
  step,
  setStep,
  formData,
  setFormData,
  stateOptions,
  stateDistricts,
  isLoadingStateDistricts,
  allSchoolDetails,
  isLoadingSchoolDetails,
  checkTeacherExists,
  handleInputChange,
  handleSubmit,
  isSubmitting,
  registrationStatus,
  registrationMessage,
  isExistingTeacher
}) {
  useEffect(() => {
    document.title = 'Teacher Registration - TAP';
  }, []);

  const [errors, setErrors] = useState({});
  const [hasClickedTapBuddy, setHasClickedTapBuddy] = useState(false);

  const config = {
    WHATSAPP_NUMBER: process.env.REACT_APP_WHATSAPP_NUMBER || '',
    WHATSAPP_MESSAGE_SUCCESS:
      process.env.REACT_APP_WHATSAPP_MESSAGE_SUCCESS_TEACHER ||
      process.env.REACT_APP_WHATSAPP_MESSAGE_SUCCESS ||
      process.env.REACT_APP_WHATSAPP_MESSAGE ||
      '',
    WHATSAPP_MESSAGE_CONERROR: process.env.REACT_APP_WHATSAPP_MESSAGE_CONERROR || '',
    WHATSAPP_MESSAGE_NOABATCH: process.env.REACT_APP_WHATSAPP_MESSAGE_NOABATCH || '',
    WHATSAPP_MESSAGE_NOSCHOOL: process.env.REACT_APP_WHATSAPP_MESSAGE_NOSCHOOL || '',
    WHATSAPP_MESSAGE_ERROR: process.env.REACT_APP_WHATSAPP_MESSAGE_ERROR || ''
  };

  const districtOptions = useMemo(() => {
    const uniqueDistricts = [
      ...new Set(
        stateDistricts
          .filter((location) => location.state === formData.state)
          .map((location) => location.district)
          .filter(Boolean)
      )
    ];

    return uniqueDistricts.sort();
  }, [formData.state, stateDistricts]);

  const cityOptions = useMemo(() => {
    const uniqueCities = [
      ...new Set(
        allSchoolDetails
          .map((school) => school.city)
          .filter(Boolean)
      )
    ];

    return uniqueCities
      .sort()
      .map((city) => ({ value: city, label: city }));
  }, [allSchoolDetails]);

  const departmentOptions = useMemo(() => {
    const uniqueDepartments = [
      ...new Set(
        allSchoolDetails
          .filter((school) => school.city === formData.city)
          .map((school) => school.department)
          .filter(Boolean)
      )
    ];

    return uniqueDepartments
      .sort()
      .map((department) => ({ value: department, label: department }));
  }, [allSchoolDetails, formData.city]);

  const schoolOptions = useMemo(() => {
    return allSchoolDetails
      .filter(
        (school) =>
          school.city === formData.city &&
          school.department === formData.department
      )
      .map((school) => ({ value: school.school_name, label: school.school_name }));
  }, [allSchoolDetails, formData.city, formData.department]);

  const noSchoolsMessage = useMemo(() => {
    if (!formData.department) {
      return '';
    }

    return schoolOptions.length === 0 ? 'No schools found for the selected criteria.' : '';
  }, [formData.department, schoolOptions]);

  const selectStyles = useMemo(() => ({
    control: (provided) => ({
      ...provided,
      minHeight: 52,
      height: 'auto',
      borderRadius: 10,
      borderColor: '#bababd',
      boxShadow: 'none',
      paddingLeft: 4,
      alignItems: 'flex-start'
    }),
    valueContainer: (provided) => ({
      ...provided,
      overflow: 'visible',
      whiteSpace: 'normal',
      paddingTop: 10,
      paddingBottom: 10
    }),
    singleValue: (provided) => ({
      ...provided,
      maxWidth: '100%',
      overflow: 'visible',
      textOverflow: 'unset',
      whiteSpace: 'normal',
      position: 'static',
      transform: 'none',
      lineHeight: 1.4,
      margin: 0
    }),
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: 'normal',
      lineHeight: 1.4
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 20
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 220
    }),
    option: (provided) => ({
      ...provided,
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'unset',
      lineHeight: 1.4
    })
  }), []);

  const updateFormData = (patch) => {
    setFormData((prev) => ({
      ...prev,
      ...patch
    }));
  };

  const handleStateChange = (event) => {
    updateFormData({
      state: event.target.value,
      district: '',
      city: '',
      department: '',
      school: ''
    });
  };

  const handleDistrictChange = (event) => {
    updateFormData({
      district: event.target.value,
      city: '',
      department: '',
      school: ''
    });
  };

  const handleCityChange = (event) => {
    updateFormData({
      city: event.target.value,
      department: '',
      school: ''
    });
  };

  const handleDepartmentChange = (event) => {
    updateFormData({
      department: event.target.value,
      school: ''
    });
  };

  const handleSchoolChange = (event) => {
    updateFormData({
      school: event.target.value
    });
  };

  const handleGradeChange = (selectedOption) => {
    const grade = selectedOption?.value || '';
    const section = grade === 'NA'
      ? 'NA'
      : formData.section === 'NA' ? '' : formData.section;

    updateFormData({ grade, section });
  };

  const handleRoleChange = (selectedOption) => {
    updateFormData({
      role: selectedOption?.value || ''
    });
  };

  const buildOptions = (values) => values.map((value) => ({ value, label: value }));

  const validatePersonalDetails = () => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = 'Teacher name is required';
    }

    if (!formData.gender) {
      nextErrors.gender = 'Gender is required';
    }

    if (!/^\d{10}$/.test(formData.phone)) {
      nextErrors.phone = 'Phone number must be 10 digits';
    }

    if (!/^\d{10}$/.test(formData.confirmPhone)) {
      nextErrors.confirmPhone = 'Confirmation number must be 10 digits';
    } else if (formData.phone !== formData.confirmPhone) {
      nextErrors.confirmPhone = 'Phone numbers do not match';
    }

    return nextErrors;
  };

  const validateSchoolLocation = () => {
    const nextErrors = {};

    if (!formData.state) nextErrors.state = 'State is required';
    if (!formData.district) nextErrors.district = 'District is required';

    return nextErrors;
  };

  const validateSchoolDetails = () => {
    const nextErrors = {};

    if (!formData.city) nextErrors.city = 'City is required';
    if (!formData.department) nextErrors.department = 'Department is required';
    if (!formData.school) nextErrors.school = 'School is required';
    if (!formData.grade) nextErrors.grade = 'Grade is required';
    if (formData.grade !== 'NA' && !formData.section.trim()) {
      nextErrors.section = 'Section / Division is required';
    }

    return nextErrors;
  };

  const handlePersonalSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validatePersonalDetails();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      const canProceed = await checkTeacherExists();
      if (canProceed) {
        setErrors({});
        setStep(2);
      }
    }
  };

  const handleSchoolLocationProceed = () => {
    const nextErrors = validateSchoolLocation();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      setErrors({});
      setStep(3);
    }
  };

  const handleSchoolProceed = () => {
    const nextErrors = validateSchoolDetails();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      setErrors({});
      setStep(4);
    }
  };

  const handleLanguageProceed = () => {
    const nextErrors = {};

    if (!formData.language) {
      nextErrors.language = 'Please select a language';
    }

    if (!formData.role) {
      nextErrors.role = 'Please select a role';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setStep(5);
  };

  const handlePreviewSubmit = async (event) => {
    event.preventDefault();

    if (!formData.agreeTerms) {
      setErrors({ agreeTerms: 'Please agree to the terms before registering' });
      return;
    }

    setErrors({});
    await handleSubmit(event);
  };

  const whatsAppLink = useMemo(() => {
    const isAlreadyRegistered =
      registrationMessage?.includes('already registered') || registrationStatus === 'already_registered';
    const isNoActiveBatch = registrationMessage?.includes('No active batch');
    const isNoSchool = registrationMessage?.includes('no school assigned');
    const isConfigError = registrationMessage?.includes('school configuration is incomplete');

    if (registrationStatus === 'success' || isAlreadyRegistered) {
      return `https://api.whatsapp.com/send?phone=${config.WHATSAPP_NUMBER}&text=${config.WHATSAPP_MESSAGE_SUCCESS}`;
    }

    if (isConfigError) {
      return `https://api.whatsapp.com/send?phone=${config.WHATSAPP_NUMBER}&text=${config.WHATSAPP_MESSAGE_CONERROR}`;
    }

    if (isNoActiveBatch) {
      return `https://api.whatsapp.com/send?phone=${config.WHATSAPP_NUMBER}&text=${config.WHATSAPP_MESSAGE_NOABATCH}`;
    }

    if (isNoSchool) {
      return `https://api.whatsapp.com/send?phone=${config.WHATSAPP_NUMBER}&text=${config.WHATSAPP_MESSAGE_NOSCHOOL}`;
    }

    return `https://api.whatsapp.com/send?phone=${config.WHATSAPP_NUMBER}&text=${config.WHATSAPP_MESSAGE_ERROR}`;
  }, [
    registrationMessage,
    registrationStatus,
    config.WHATSAPP_NUMBER,
    config.WHATSAPP_MESSAGE_SUCCESS,
    config.WHATSAPP_MESSAGE_CONERROR,
    config.WHATSAPP_MESSAGE_NOABATCH,
    config.WHATSAPP_MESSAGE_NOSCHOOL,
    config.WHATSAPP_MESSAGE_ERROR
  ]);

  useEffect(() => {
    if (registrationStatus !== 'success' || hasClickedTapBuddy) {
      return undefined;
    }

    const redirectTimer = window.setTimeout(() => {
      window.location.assign(whatsAppLink);
    }, 5000);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [hasClickedTapBuddy, registrationStatus, whatsAppLink]);

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <form className="teacher-card-body" onSubmit={handlePersonalSubmit}>
          <span className="teacher-section-title">PERSONAL DETAILS</span>

          <div className="teacher-form-grid">
            <label className="teacher-form-field">
              <span className="teacher-field-label">TEACHER NAME</span>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="teacher-input"
                placeholder="Eg: Sonam Kumari"
              />
              {errors.name && <span className="error">{errors.name}</span>}
            </label>

            <label className="teacher-form-field">
              <span className="teacher-field-label">GENDER</span>
              <Select
                className="teacher-select"
                classNamePrefix="teacher-select"
                styles={selectStyles}
                options={buildOptions(genderOptions)}
                value={formData.gender ? { value: formData.gender, label: formData.gender } : null}
                onChange={(selectedOption) => {
                  handleInputChange({ target: { name: 'gender', value: selectedOption?.value || '' } });
                }}
                placeholder="Select gender"
              />
              {errors.gender && <span className="error">{errors.gender}</span>}
            </label>

            <label className="teacher-form-field">
              <span className="teacher-field-label">WHATSAPP NUMBER</span>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="teacher-input"
                placeholder="10 digit mobile number"
                maxLength="10"
              />
              {errors.phone && <span className="error">{errors.phone}</span>}
            </label>

            <label className="teacher-form-field">
              <span className="teacher-field-label">CONFIRM WHATSAPP NUMBER</span>
              <input
                type="tel"
                name="confirmPhone"
                value={formData.confirmPhone}
                onChange={handleInputChange}
                className="teacher-input"
                placeholder="Re-enter 10 digit mobile number"
                maxLength="10"
              />
              {errors.confirmPhone && <span className="error">{errors.confirmPhone}</span>}
            </label>
          </div>

          <button type="submit" className="teacher-primary-button" disabled={isSubmitting}>
            <span className="teacher-primary-button-text">PROCEED</span>
          </button>
        </form>
      );
    }

    if (step === 2) {
      return (
        <div className="teacher-card-body">
          <span className="teacher-section-title">SCHOOL LOCATION</span>
          <p className="teacher-helper-text">Select the state and district where your school is located.</p>

          <div className="teacher-form-grid">
            <label className="teacher-form-field">
              <span className="teacher-field-label">SCHOOL STATE</span>
              <Select
                className="teacher-select"
                classNamePrefix="teacher-select"
                styles={selectStyles}
                options={buildOptions(stateOptions)}
                value={formData.state ? { value: formData.state, label: formData.state } : null}
                onChange={(selectedOption) => {
                  handleStateChange({ target: { value: selectedOption?.value || '' } });
                }}
                placeholder={isLoadingStateDistricts ? 'Loading states...' : 'Select state'}
                isDisabled={isLoadingStateDistricts}
              />
              {errors.state && <span className="error">{errors.state}</span>}
            </label>

            <label className="teacher-form-field">
              <span className="teacher-field-label">SCHOOL DISTRICT / ZONE</span>
              <Select
                className="teacher-select"
                classNamePrefix="teacher-select"
                styles={selectStyles}
                options={buildOptions(districtOptions)}
                value={formData.district ? { value: formData.district, label: formData.district } : null}
                onChange={(selectedOption) => {
                  handleDistrictChange({ target: { value: selectedOption?.value || '' } });
                }}
                placeholder={isLoadingStateDistricts ? 'Loading districts...' : 'Select district'}
                isDisabled={!formData.state || isLoadingStateDistricts}
              />
              {errors.district && <span className="error">{errors.district}</span>}
            </label>

          </div>

          <button type="button" className="teacher-primary-button" onClick={handleSchoolLocationProceed}>
            <span className="teacher-primary-button-text">PROCEED</span>
          </button>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="teacher-card-body">
          <span className="teacher-section-title">SCHOOL DETAILS</span>
          <p className="teacher-helper-text">Select the school information linked to your registration.</p>

          <div className="teacher-form-grid">
            <label className="teacher-form-field">
              <span className="teacher-field-label">SCHOOL CITY / BLOCK / WARD</span>
              <Select
                className="teacher-select"
                classNamePrefix="teacher-select"
                styles={selectStyles}
                options={cityOptions}
                value={formData.city ? { value: formData.city, label: formData.city } : null}
                onChange={(selectedOption) => {
                  handleCityChange({ target: { value: selectedOption?.value || '' } });
                }}
                placeholder={isLoadingSchoolDetails ? 'Loading cities...' : 'Select city'}
                isDisabled={!formData.district || isLoadingSchoolDetails}
              />
              {errors.city && <span className="error">{errors.city}</span>}
            </label>

            <label className="teacher-form-field">
              <span className="teacher-field-label">DEPARTMENT</span>
              <Select
                className="teacher-select"
                classNamePrefix="teacher-select"
                styles={selectStyles}
                options={departmentOptions}
                value={formData.department ? { value: formData.department, label: formData.department } : null}
                onChange={(selectedOption) => {
                  handleDepartmentChange({ target: { value: selectedOption?.value || '' } });
                }}
                placeholder="Select department"
                isDisabled={!formData.city || isLoadingSchoolDetails}
              />
              {errors.department && <span className="error">{errors.department}</span>}
            </label>

            <label className="teacher-form-field">
              <span className="teacher-field-label">SCHOOL NAME</span>
              <Select
                className="teacher-select"
                classNamePrefix="teacher-select"
                styles={selectStyles}
                options={schoolOptions}
                value={formData.school ? { value: formData.school, label: formData.school } : null}
                onChange={(selectedOption) => {
                  handleSchoolChange({ target: { value: selectedOption?.value || '' } });
                }}
                placeholder={isLoadingSchoolDetails ? 'Loading schools...' : 'Select school'}
                isDisabled={!formData.department || isLoadingSchoolDetails}
              />
              {noSchoolsMessage && <span className="error">{noSchoolsMessage}</span>}
              {errors.school && <span className="error">{errors.school}</span>}
            </label>

            <label className="teacher-form-field">
              <span className="teacher-field-label">GRADE</span>
              <Select
                className="teacher-select"
                classNamePrefix="teacher-select"
                styles={selectStyles}
                options={buildOptions(gradeOptions)}
                value={formData.grade ? { value: formData.grade, label: formData.grade } : null}
                onChange={handleGradeChange}
                placeholder="Select grade"
              />
              {errors.grade && <span className="error">{errors.grade}</span>}
            </label>

            <label className="teacher-form-field">
              <span className="teacher-field-label">SECTION / DIVISION</span>
              <input
                type="text"
                name="section"
                value={formData.section}
                onChange={handleInputChange}
                className="teacher-input"
                placeholder="Eg: A"
                maxLength="140"
                disabled={formData.grade === 'NA'}
              />
              {errors.section && <span className="error">{errors.section}</span>}
            </label>
          </div>

          <button type="button" className="teacher-primary-button" onClick={handleSchoolProceed}>
            <span className="teacher-primary-button-text">PROCEED</span>
          </button>
        </div>
      );
    }

    if (step === 4) {
      return (
        <div className="teacher-card-body">
          <span className="teacher-section-title">TEACHER DETAILS</span>

          <label className="teacher-form-field">
            <span className="teacher-field-label">YOUR ROLE</span>
            <Select
              className="teacher-select"
              classNamePrefix="teacher-select"
              styles={selectStyles}
              options={buildOptions(roleOptions)}
              value={formData.role ? { value: formData.role, label: formData.role } : null}
              onChange={handleRoleChange}
              placeholder="Select Role"
            />
            {errors.role && <span className="error">{errors.role}</span>}
          </label>

          <span className="teacher-section-title">LANGUAGE PREFERENCE</span>
          <p className="teacher-helper-text">Select the language you would like to use with TAP Buddy.</p>

          <div className="teacher-radio-grid">
            {languageOptions.map((language) => (
              <label key={language} className="teacher-radio-option">
                <input
                  type="radio"
                  name="language"
                  value={language}
                  checked={formData.language === language}
                  onChange={handleInputChange}
                />
                <span>{language}</span>
              </label>
            ))}
          </div>

          {errors.language && <span className="error">{errors.language}</span>}

          <button type="button" className="teacher-primary-button" onClick={handleLanguageProceed}>
            <span className="teacher-primary-button-text">PROCEED</span>
          </button>
        </div>
      );
    }

    if (step === 5) {
      return (
        <form className="teacher-card-body" onSubmit={handlePreviewSubmit}>
          <span className="teacher-section-title">REVIEW AND CONSENT</span>
          <p className="teacher-helper-text">
            {isExistingTeacher
              ? 'Your saved teacher details were found. Review them and edit anything before updating.'
              : 'Check every detail before registering.'}
          </p>

          <div className="teacher-preview-section">
            <div className="teacher-preview-header">
              <span className="teacher-preview-title">PERSONAL DETAILS</span>
              <button type="button" className="teacher-inline-button" onClick={() => setStep(1)}>
                Edit
              </button>
            </div>
            <div className="teacher-preview-grid">
              <div><strong>Name:</strong> {formData.name}</div>
              <div><strong>Gender:</strong> {formData.gender}</div>
              <div><strong>Phone:</strong> {formData.phone}</div>
            </div>
          </div>

          <div className="teacher-preview-section">
            <div className="teacher-preview-header">
              <span className="teacher-preview-title">SCHOOL LOCATION</span>
              <button type="button" className="teacher-inline-button" onClick={() => setStep(2)}>
                Edit
              </button>
            </div>
            <div className="teacher-preview-grid">
              <div><strong>State:</strong> {formData.state}</div>
              <div><strong>District:</strong> {formData.district}</div>
            </div>
          </div>

          <div className="teacher-preview-section">
            <div className="teacher-preview-header">
              <span className="teacher-preview-title">SCHOOL DETAILS</span>
              <button type="button" className="teacher-inline-button" onClick={() => setStep(3)}>
                Edit
              </button>
            </div>
            <div className="teacher-preview-grid">
              <div><strong>City:</strong> {formData.city}</div>
              <div><strong>Department:</strong> {formData.department}</div>
              <div><strong>School:</strong> {formData.school}</div>
              <div><strong>Grade:</strong> {formData.grade}</div>
              <div><strong>Section / Division:</strong> {formData.section}</div>
            </div>
          </div>

          <div className="teacher-preview-section">
            <div className="teacher-preview-header">
              <span className="teacher-preview-title">LANGUAGE AND ROLE</span>
              <button type="button" className="teacher-inline-button" onClick={() => setStep(4)}>
                Edit
              </button>
            </div>
            <div className="teacher-preview-grid">
              <div><strong>Language:</strong> {formData.language}</div>
              <div><strong>Role:</strong> {formData.role}</div>
            </div>
          </div>

          <label className="teacher-consent-row">
            <input
              type="checkbox"
              id="agreeTerms"
              name="agreeTerms"
              checked={formData.agreeTerms}
              onChange={handleInputChange}
            />
            <span>I agree to receive messages from TAP Buddy and ensure to not block it.</span>
          </label>

          {errors.agreeTerms && <span className="error">{errors.agreeTerms}</span>}

          <button type="submit" className="teacher-primary-button" disabled={isSubmitting}>
            <span className="teacher-primary-button-text">
              {isSubmitting ? (isExistingTeacher ? 'UPDATING...' : 'REGISTERING...') : (isExistingTeacher ? 'UPDATE DETAILS' : 'REGISTER')}
            </span>
          </button>
        </form>
      );
    }

    return (
      registrationStatus === 'success' ? (
        <div className="teacher-success-screen">
          <div className="teacher-success-copy">
            <h2 className="teacher-success-title">One Last Step 😇</h2>
            <p className="teacher-success-message">Thank you for sharing your details!</p>
            <p className="teacher-success-message">
              Click the button below to finish your registration with TAP Buddy.
            </p>
          </div>

          <div className="teacher-click-card" aria-hidden="true">
            <img
              src={`${process.env.PUBLIC_URL || ''}/external/click-me.svg`}
              alt=""
              className="teacher-click-asset"
            />
          </div>

          <a
            href={whatsAppLink}
            target="_blank"
            rel="noreferrer noopener"
            className="teacher-primary-button teacher-link-button teacher-success-button"
            onClick={() => setHasClickedTapBuddy(true)}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/external/whatsapp-icon.svg`}
              alt="WhatsApp"
              className="teacher-whatsapp-icon"
            />
            <span className="teacher-primary-button-text">Finish Registration</span>
          </a>
        </div>
      ) : (
        <div className="teacher-card-body teacher-status-body">
          <span className="teacher-section-title">REGISTRATION STATUS</span>
          <p className="teacher-helper-text">
            {registrationMessage || 'Registration processing has completed.'}
          </p>
          <a
            href={whatsAppLink}
            target="_blank"
            rel="noreferrer noopener"
            className="teacher-primary-button teacher-link-button"
          >
            <span className="teacher-primary-button-text">CONTACT SUPPORT</span>
          </a>
        </div>
      )
    );
  };

  return (
    <div className="teacher-page-shell">
      <div className="teacher-card">
        <span className="teacher-brand">
          THE <span className="teacher-brand-highlight">APPRENTICE</span> PROJECT
        </span>
        <p className="teacher-page-title">Teacher Registration</p>
        <p className="teacher-page-subtitle">Fill this form to register with TAP Buddy.</p>

        <div className="teacher-progress">
          {[1, 2, 3, 4, 5].map((progressStep) => (
            <div
              key={progressStep}
              className={`teacher-progress-step ${progressStep <= Math.min(step, 5) ? 'active' : ''}`}
            >
              {progressStep}
            </div>
          ))}
        </div>

        {renderStepContent()}
      </div>
    </div>
  );
}

export default TeacherRegistrationPage;
